import { Button } from '@/global/components/ui/button'
import { PopoverTrigger } from '@/global/components/ui/popover'
import {
    inputVariants,
    realceCampoAberto,
    rotuloCampoClass,
} from '@/global/components/ui/input'
import { cn } from '@/lib/utils'
import { ChevronDownIcon } from 'lucide-react'
import { ICONES_FACETA } from './Cascata'
import { useEscopoContext } from './context'
import { type FacetaId, palavraContagem } from './escopo'

export type TriggerProps = {
    className?: string
    label?: string
}

const TEXTO_PRINCIPAL =
    'min-w-0 flex-1 truncate text-left text-base font-normal leading-[23px] text-foreground'

/**
 * Pílula de uma faceta: colapsada mostra ícone + contagem. No hover cresce e
 * revela o tipo do filtro ao lado (`1 centro de custo`). Sem hover, o tipo de
 * cada faceta também aparece no rail do painel (`Cascata`).
 *
 * `prefixo` é o `+` do badge de empresas, decidido em `montarResumoVisual`
 * (`+2`, `+2 empresas`).
 */
function PilulaFaceta({
    id,
    total,
    prefixo = '',
}: {
    id: FacetaId
    total: number
    prefixo?: string
}) {
    const Icone = ICONES_FACETA[id]

    return (
        <span
            className={cn(
                'group/badge flex shrink-0 items-center',
                'whitespace-nowrap rounded-full border',
                'border-accent bg-primary/10',
                'px-1.5 py-0.5 text-xs font-medium text-primary',
                'transition-[padding] duration-200 hover:px-2',
            )}
        >
            <Icone className="size-3.5 shrink-0" />
            <span className="pl-1 tabular-nums">{`${prefixo}${total}`}</span>
            <span
                className={cn(
                    'grid grid-cols-[0fr] group-hover/badge:grid-cols-[1fr]',
                    'transition-[grid-template-columns] duration-200',
                )}
            >
                <span className="min-w-0 overflow-hidden">
                    <span className="pl-1">{palavraContagem(id, total)}</span>
                </span>
            </span>
        </span>
    )
}

/**
 * Botão do filtro, de largura fixa: nome da empresa em texto truncado + um
 * badge por faceta com seleção. Só o nome da empresa cede espaço.
 *
 * Caixa, underline e realce vêm de {@link inputVariants}, os mesmos do
 * `Combobox` e do `SelectPeriodo` que ficam ao lado dele nos relatórios.
 */
export function Trigger({ className, label = 'Filtro' }: TriggerProps) {
    const { resumoVisual, aberto } = useEscopoContext()
    const {
        empresaTexto,
        funcionarioTexto,
        empresasExtras,
        empresasPrefixo,
        badges,
    } = resumoVisual
    // em tela de um funcionário só, é o nome dele que o botão mostra (#1870)
    const textoPrincipal = funcionarioTexto ?? empresaTexto

    const temBadges = empresasExtras > 0 || badges.length > 0

    const listaBadges = temBadges ? (
        <div className="mb-1 flex shrink-0 items-center gap-1">
            {empresasExtras > 0 && (
                <PilulaFaceta
                    id="empresas"
                    total={empresasExtras}
                    prefixo={empresasPrefixo}
                />
            )}
            {badges.map(badge => (
                <PilulaFaceta
                    key={badge.id}
                    id={badge.id}
                    total={badge.total}
                />
            ))}
        </div>
    ) : null

    const conteudo = temBadges ? (
        <>
            {textoPrincipal && (
                <span className={TEXTO_PRINCIPAL}>{textoPrincipal}</span>
            )}
            {listaBadges}
        </>
    ) : (
        <span className={TEXTO_PRINCIPAL}>
            {textoPrincipal ?? 'Nenhum filtro'}
        </span>
    )

    return (
        <PopoverTrigger asChild>
            <Button
                type="button"
                variant="ghost"
                aria-label="Filtro do relatório"
                className={cn(
                    'group/trigger',
                    inputVariants({ size: 'small' }),
                    // O `ghost` do botão pinta o hover por conta própria —
                    // inclusive no escuro — e brigaria com o da caixa.
                    'hover:text-foreground dark:hover:bg-muted/95',
                    'w-[27.5rem] shrink-0 justify-between gap-3 font-normal',
                    aberto && realceCampoAberto.filled,
                    className,
                )}
            >
                <div className="flex min-w-0 flex-1 flex-col text-left">
                    <span className={cn(rotuloCampoClass, 'mt-1')}>
                        {label}
                    </span>
                    <div className="flex min-w-0 items-center -mt-0.5 gap-1.5 leading-[23px]">
                        {conteudo}
                    </div>
                </div>
                <ChevronDownIcon className="size-5 shrink-0 text-foreground/55 transition-transform duration-200 group-data-[state=open]/trigger:rotate-180" />
            </Button>
        </PopoverTrigger>
    )
}
